import type { TooltipComponentOption } from 'echarts';

/** Folga mínima entre o tooltip e as bordas da tela. */
export const TOOLTIP_VIEWPORT_MARGIN = 8;

/** Distância entre o cursor e o tooltip. */
const CURSOR_GAP = 12;

/**
 * Layer único onde todo tooltip de gráfico é renderizado. Declarado no
 * app-shell; ver `chartTooltipLayer()` para o fallback.
 */
export const CHART_TOOLTIP_LAYER_ID = 'chart-tooltip-layer';

/**
 * Classes do layer. O `z-index` acompanha o que o ECharts aplica no próprio
 * tooltip (9999999) e precisa ficar aqui: o layer cria contexto de empilhamento,
 * então é ele que disputa a pilha — acima dos overlays do CDK (1000), para o
 * tooltip aparecer também quando o gráfico está dentro de um diálogo.
 */
export const CHART_TOOLTIP_LAYER_CLASS =
  'fixed inset-0 z-[9999999] pointer-events-none';

/**
 * Limites do box do tooltip. O tooltip vive num layer que cobre a viewport,
 * então `100%` aqui é a tela — não o card do gráfico. `white-space` e
 * `text-align` são declarados porque o ECharts usa `nowrap` por padrão e o
 * layer herdaria o alinhamento do body.
 *
 * Concatene em qualquer `tooltip.extraCssText` próprio; o tema já aplica para
 * os gráficos que não definem o seu.
 */
export const TOOLTIP_VIEWPORT_CSS =
  'max-width: calc(100% - 16px); max-height: calc(100% - 16px); overflow: hidden; white-space: normal; text-align: start;';

interface Size {
  readonly width: number;
  readonly height: number;
}

/**
 * Posição do tooltip em coordenadas do gráfico, garantindo que o box inteiro
 * caiba na viewport.
 *
 * O ECharts pede a posição no espaço do gráfico, mas o recorte que importa é o
 * da tela — daí a conversão via `chartRect`. Quando o lado preferido (abaixo e à
 * direita do cursor) não cabe, o tooltip vira para o lado oposto; se nenhum dos
 * dois cabe, encosta na borda respeitando a margem.
 */
export function clampToViewport(
  point: readonly [number, number],
  contentSize: readonly [number, number],
  chartRect: { readonly left: number; readonly top: number } | undefined,
  viewport: Size,
  margin = TOOLTIP_VIEWPORT_MARGIN,
): [number, number] {
  const axis = (
    cursor: number,
    offset: number,
    content: number,
    available: number,
  ): number => {
    const after = cursor + CURSOR_GAP;
    const before = cursor - content - CURSOR_GAP;
    const fits = (start: number) =>
      start >= margin && start + content <= available - margin;
    const preferred = fits(after) || !fits(before) ? after : before;
    const max = Math.max(margin, available - content - margin);
    return Math.min(Math.max(preferred, margin), max) - offset;
  };

  const offsetX = chartRect?.left ?? 0;
  const offsetY = chartRect?.top ?? 0;
  return [
    axis(offsetX + point[0], offsetX, contentSize[0], viewport.width),
    axis(offsetY + point[1], offsetY, contentSize[1], viewport.height),
  ];
}

/**
 * Layer da app. Fica no app-shell, mas gráficos renderizados fora dele (um
 * diálogo, um teste) também precisam de um — daí o fallback idempotente.
 */
function chartTooltipLayer(): HTMLElement {
  const existing = document.getElementById(CHART_TOOLTIP_LAYER_ID);
  if (existing) return existing;

  const layer = document.createElement('div');
  layer.id = CHART_TOOLTIP_LAYER_ID;
  layer.setAttribute('aria-hidden', 'true');
  layer.className = CHART_TOOLTIP_LAYER_CLASS;
  document.body.appendChild(layer);
  return layer;
}

/** Gráfico dono de cada tooltip do layer — o clamp precisa do rect dele. */
const chartByTooltip = new WeakMap<Element, HTMLElement>();

function claimTooltipFor(chartDom: HTMLElement, layer: HTMLElement): void {
  const previous = new Set(layer.children);
  // O ECharts anexa o tooltip ao layer imediatamente após resolver `appendTo`;
  // a microtask roda depois disso e identifica qual elemento é deste gráfico.
  queueMicrotask(() => {
    for (const child of layer.children) {
      if (!previous.has(child)) chartByTooltip.set(child, chartDom);
    }
  });
}

/**
 * Tooltip que nunca ultrapassa a viewport e nunca é recortado pelo card.
 *
 * Aplicado pelo tema `audaces`, portanto vale para todo gráfico da app. O
 * `confine` do ECharts fica desligado de propósito: ele confina à área do
 * gráfico — recortando tooltips maiores que o card — e rodaria depois do
 * `position`, desfazendo o clamp de viewport.
 */
export const TOOLTIP_VIEWPORT: TooltipComponentOption = {
  confine: false,
  appendTo: (chartDom: HTMLElement) => {
    const layer = chartTooltipLayer();
    claimTooltipFor(chartDom, layer);
    return layer;
  },
  position: (point, _params, dom, _rect, size) => {
    const chartDom = chartByTooltip.get(dom as HTMLElement);
    return clampToViewport(
      point as [number, number],
      size.contentSize as [number, number],
      chartDom?.getBoundingClientRect(),
      { width: window.innerWidth, height: window.innerHeight },
    );
  },
  extraCssText: TOOLTIP_VIEWPORT_CSS,
};
